/*
 * The one shape every rail control takes: an icon with its label beside it.
 *
 * Split into strings rather than written inline because `NavLink` and, until 2026-08-14, `NavAction` and `ThemeToggle`
 * must be indistinguishable — one is an anchor, two are buttons, and the difference is a fact about
 * markup that the eye should never be able to detect. Three copies of this would drift.
 *
 * `IDLE` is the resting state for BOTH a link that is not current and the two buttons, which are
 * never current. Only a link can be `ACTIVE`.
 *
 * ==========================================================================================
 * ONE SHAPE AT EVERY WIDTH, SINCE 2026-08-21
 * ==========================================================================================
 *
 * It used to be a full-width row above `md` and a 48px circle below it, because the phone had a
 * BOTTOM BAR where nine controls shared one line and nine words could not fit. That bar is gone —
 * `side-nav.tsx` has the arithmetic and the two bugs it caused — and the phone now gets a drawer,
 * which is a column with room for the labels.
 *
 * So there is no icon-only context left in the application, and the breakpoint half of these strings
 * went with it. `aria-label` STAYS on every control regardless: the visible label and the announced
 * name being the same string is what 47 e2e locators depend on, and it costs nothing to keep true.
 */

/** Structure and the hover transition. The group is what lets the icon inside lift on hover. */
export const NAV_PILL_BASE =
  "group flex h-10 w-full cursor-pointer items-center justify-start gap-3 rounded-lg px-3 " +
  "transition-all duration-300 ease-in-out";

/*
 * Dimmed until pointed at. The dimming is what makes the current page legible at a glance.
 *
 * 60%, NOT THE 30% AN ICON-ONLY RAIL COULD AFFORD. A glyph at 30% opacity is a dimmer glyph; a word
 * at 30% is a word you have to lean in to read, and there are now nine of them in a column. The
 * active row is still unmistakable — it is the only one with a fill behind it.
 */
export const NAV_PILL_IDLE = "opacity-60 hover:bg-accent hover:opacity-100";

/*
 * The original used an opaque `#12151A` fill here. This app renders over a live WebGL gradient, so
 * an opaque fill would punch a visible rectangle out of the waves — `bg-accent` is the translucent
 * white overlay tokens.css defines for exactly this, and it tints the gradient instead.
 */
export const NAV_PILL_ACTIVE = "bg-accent opacity-100";

/** The icon lifts, not the pill: the pill is the hit area and moving it would move the target. */
export const NAV_ICON_LIFT = "shrink-0 transition duration-300 ease-out group-hover:-translate-y-1";

/**
 * 24px, at both breakpoints.
 *
 * It used to be 34px on the desktop rail — sized to fill a 64px circle that was the whole control.
 * With a label beside it the icon is no longer the control, it is the mark in front of one, and an
 * icon taller than its own cap height reads as an illustration rather than as an affordance.
 */
export const NAV_ICON = "size-6 text-ink";

/**
 * The label, now shown at every width.
 *
 * It was `hidden … md:block` for the phone bar, which had no room for it. Nothing hides it any more:
 * the drawer that replaced that bar is a column and the labels are most of why it is better.
 *
 * `truncate` because a section name is a fact about the roadmap, not about the rail's width — a
 * future entry longer than the column should clip rather than wrap a nav row to two lines.
 */
export const NAV_LABEL = "truncate text-sm font-medium";
